import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faChartColumn,
    faCheck,
    faChevronDown,
    faChevronRight,
    faEllipsisV,
    faPause,
    faPencil,
    faPlay,
    faPlus,
    faRotateLeft,
    faLayerGroup,
    faStopwatch,
    faTimes,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Collection, Stopwatch, StopwatchStep } from "../../types";
import { generateId } from "../../utils/Utils";
import ActionMenu from "../action-menu/ActionMenu";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import ListSelectionDialog from "../list-selection-dialog/ListSelectionDialog";
import "./TimerElement.css";

interface StopwatchElementProps {
    element: Stopwatch;
    deleteElement: (id: string) => void;
    updateElement: (updatedElement: Stopwatch) => void;
    lists: Collection[];
    selectedListId: string;
    moveElementToList: (element: Stopwatch, listId: string) => void;
    onMoveToSection?: (element: Stopwatch) => void;
}

type RecordedStep = { elapsedTime: number; status: 'completed' | 'incomplete' };

export const StopwatchElement: React.FC<StopwatchElementProps> = ({
    element,
    deleteElement,
    updateElement,
    lists,
    selectedListId,
    moveElementToList,
    onMoveToSection,
}) => {
    const { t, i18n } = useTranslation();
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [pausedTime, setPausedTime] = useState(0);
    const [stepsCollapsed, setStepsCollapsed] = useState(true);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showStatsDialog, setShowStatsDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showListDialog, setShowListDialog] = useState(false);
    const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState(element.title);
    const [editMode, setEditMode] = useState<'simple' | 'steps'>(element.mode === 'simple' ? 'simple' : 'steps');
    const [editSteps, setEditSteps] = useState<StopwatchStep[]>(element.steps || []);
    const [newStep, setNewStep] = useState('');
    const sessionStartedAtRef = useRef<string | null>(null);
    const sessionPausedTimeRef = useRef(0);
    const sessionStepsRef = useRef<Record<string, RecordedStep>>({});

    const steps = element.steps || [];
    const isSimpleMode = element.mode === 'simple';
    const currentStep = steps[currentStepIndex];
    const completedCount = steps.filter((step) => step.completed).length;
    const isRoutineComplete = steps.length > 0 && completedCount === steps.length;
    const progress = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

    useEffect(() => {
        if (!isPlaying) return;
        const interval = window.setInterval(() => setElapsedTime((time) => time + 1), 1000);
        return () => window.clearInterval(interval);
    }, [isPlaying, currentStepIndex]);

    useEffect(() => {
        if (isPlaying || !hasStarted || isRoutineComplete) return;
        const interval = window.setInterval(() => {
            setPausedTime((time) => time + 1);
            sessionPausedTimeRef.current += 1;
        }, 1000);
        return () => window.clearInterval(interval);
    }, [hasStarted, isPlaying, isRoutineComplete]);

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return hours > 0
            ? `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
            : `${minutes}:${String(secs).padStart(2, '0')}`;
    };

    const formatDate = (date: string) => new Intl.DateTimeFormat(
        i18n.language?.startsWith('ro') ? 'ro-RO' : 'en-US',
        { dateStyle: 'medium', timeStyle: 'short' }
    ).format(new Date(date));

    const clearSessionRuntime = () => {
        sessionStartedAtRef.current = null;
        sessionPausedTimeRef.current = 0;
        sessionStepsRef.current = {};
    };

    const handlePlayPause = () => {
        if (!currentStep) return;
        if (!isPlaying && isRoutineComplete) {
            updateElement({ ...element, steps: steps.map((step) => ({ ...step, completed: false })) });
            setCurrentStepIndex(0);
            setElapsedTime(0);
            setPausedTime(0);
            clearSessionRuntime();
        }
        if (!isPlaying) {
            if (!sessionStartedAtRef.current) sessionStartedAtRef.current = new Date().toISOString();
            setHasStarted(true);
        }
        setIsPlaying((playing) => !playing);
    };

    const handleCompleteStep = () => {
        if (!currentStep) return;
        const nextRecords = {
            ...sessionStepsRef.current,
            [currentStep.id]: { elapsedTime, status: 'completed' as const },
        };
        sessionStepsRef.current = nextRecords;
        const updatedSteps = steps.map((step, index) => index === currentStepIndex
            ? { ...step, completed: true, timeHistory: [...(step.timeHistory || []), elapsedTime] }
            : step
        );

        if (currentStepIndex < steps.length - 1) {
            updateElement({ ...element, steps: updatedSteps });
            setCurrentStepIndex(currentStepIndex + 1);
            setElapsedTime(0);
            setPausedTime(0);
            setHasStarted(isPlaying);
            return;
        }

        const completedAt = new Date().toISOString();
        const sessionSteps = updatedSteps.map((step) => {
            const record = nextRecords[step.id];
            return {
                stepId: step.id,
                text: step.text,
                elapsedTime: record?.elapsedTime || 0,
                status: record?.status || 'skipped' as const,
            };
        });
        const today = completedAt.slice(0, 10);
        updateElement({
            ...element,
            steps: updatedSteps,
            completedDates: element.completedDates.includes(today)
                ? element.completedDates
                : [...element.completedDates, today],
            sessionHistory: [
                ...(element.sessionHistory || []),
                {
                    id: generateId(),
                    startedAt: sessionStartedAtRef.current || completedAt,
                    completedAt,
                    completed: true,
                    pausedTime: sessionPausedTimeRef.current,
                    totalTime: sessionSteps.reduce((total, step) => total + step.elapsedTime, 0),
                    steps: sessionSteps,
                },
            ],
        });
        setIsPlaying(false);
        setHasStarted(false);
        setPausedTime(0);
        clearSessionRuntime();
    };

    const handleReset = () => {
        const resetAt = new Date().toISOString();
        const records = { ...sessionStepsRef.current };
        if (currentStep && elapsedTime > 0) {
            records[currentStep.id] = { elapsedTime, status: 'incomplete' };
        }
        const sessionSteps = steps.map((step) => ({
            stepId: step.id,
            text: step.text,
            elapsedTime: records[step.id]?.elapsedTime || 0,
            status: records[step.id]?.status || 'skipped' as const,
        }));
        const totalTime = sessionSteps.reduce((total, step) => total + step.elapsedTime, 0);
        const shouldRecord = Boolean(sessionStartedAtRef.current && (totalTime > 0 || sessionPausedTimeRef.current > 0));
        updateElement({
            ...element,
            steps: steps.map((step) => ({ ...step, completed: false })),
            sessionHistory: shouldRecord ? [
                ...(element.sessionHistory || []),
                {
                    id: generateId(),
                    startedAt: sessionStartedAtRef.current || resetAt,
                    completedAt: resetAt,
                    completed: false,
                    pausedTime: sessionPausedTimeRef.current,
                    totalTime,
                    steps: sessionSteps,
                },
            ] : element.sessionHistory,
        });
        setIsPlaying(false);
        setHasStarted(false);
        setCurrentStepIndex(0);
        setElapsedTime(0);
        setPausedTime(0);
        clearSessionRuntime();
    };

    const openEdit = () => {
        setEditTitle(element.title);
        setEditMode(element.mode === 'simple' ? 'simple' : 'steps');
        setEditSteps(element.steps || []);
        setNewStep('');
        setShowEditDialog(true);
    };

    const saveEdit = () => {
        const validSteps = editSteps.filter((step) => step.text.trim());
        const simpleStep = editSteps[0] || { id: generateId(), text: editTitle, timeHistory: [], completed: false };
        updateElement({
            ...element,
            title: editTitle.trim() || element.title,
            mode: editMode,
            steps: editMode === 'simple'
                ? [{ ...simpleStep, text: editTitle.trim() || element.title }]
                : validSteps.length > 0
                    ? validSteps
                    : [{ id: generateId(), text: t('addStopwatch.defaultStep'), timeHistory: [], completed: false }],
        });
        setShowEditDialog(false);
    };

    const sessions = [...(element.sessionHistory || [])].sort(
        (a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
    );
    const allTimes = steps.flatMap((step) => step.timeHistory || []);
    const totalTracked = allTimes.reduce((total, time) => total + time, 0)
        + sessions.filter((session) => !session.completed).reduce((total, session) => total + session.totalTime, 0);
    const averageSession = sessions.filter((session) => session.completed).length > 0
        ? Math.round(sessions.filter((session) => session.completed).reduce((total, session) => total + session.totalTime, 0)
            / sessions.filter((session) => session.completed).length)
        : 0;

    const actions = [
        { label: t('stopwatchElement.statistics'), icon: faChartColumn, callback: () => setShowStatsDialog(true) },
        { label: t('stopwatchElement.edit'), icon: faPencil, callback: openEdit },
        { label: t('stopwatchElement.move'), icon: faArrowRight, callback: () => setShowListDialog(true) },
        ...(onMoveToSection ? [{ label: t('sections.moveToSection'), icon: faLayerGroup, callback: () => onMoveToSection(element) }] : []),
        { label: t('stopwatchElement.delete'), icon: faTrash, callback: () => setShowDeleteDialog(true) },
    ];

    return (
        <div className="timer-element stopwatch-element">
            <div className="timer-header">
                <div className="timer-header-left">
                    <FontAwesomeIcon icon={faStopwatch} className="timer-header-clock" />
                    <div className="timer-header-text">
                        <span className="element-type-label stopwatch-type-label">{t('elementTypes.stopwatch')}</span>
                        <span className="timer-header-title">{element.title}</span>
                        {!isSimpleMode && <span className="timer-doing-task">{currentStep?.text || t('stopwatchElement.noStep')}</span>}
                    </div>
                </div>
                <div className="timer-header-right">
                    <span className="timer-main-time">{formatTime(elapsedTime)}</span>
                    <button className="timer-play-button" onClick={handlePlayPause} aria-label={isPlaying ? t('stopwatchElement.pause') : t('stopwatchElement.start')}>
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>
                    <button className="timer-complete-step-button active" onClick={handleCompleteStep} disabled={!currentStep} aria-label={t('stopwatchElement.completeStep')}>
                        <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button className="timer-reset-button" onClick={handleReset} aria-label={t('stopwatchElement.reset')} title={t('stopwatchElement.reset')}>
                        <FontAwesomeIcon icon={faRotateLeft} />
                    </button>
                    <ActionMenu actions={actions} icon={faEllipsisV} buttonClassName="timer-menu-button" buttonStyle={{}} />
                </div>
            </div>

            {!isSimpleMode && <div className="timer-progress-row">
                <div className="timer-progress-copy">
                    <span>{t('stopwatchElement.stepProgress', { current: steps.length ? currentStepIndex + 1 : 0, total: steps.length })}</span>
                    <span>{!isPlaying && pausedTime > 0 ? t('stopwatchElement.pausedFor', { time: formatTime(pausedTime) }) : t('stopwatchElement.progress', { percent: progress })}</span>
                </div>
                <div className="timer-progress-track" aria-label={t('stopwatchElement.progress', { percent: progress })}>
                    <div className="timer-progress-fill" style={{ width: `${progress}%` }} />
                </div>
            </div>}

            {!isSimpleMode && <div className="timer-subtimers-section">
                <button type="button" className="timer-subtimers-header stopwatch-steps-toggle" onClick={() => setStepsCollapsed((collapsed) => !collapsed)}>
                    <FontAwesomeIcon icon={stepsCollapsed ? faChevronRight : faChevronDown} className="subtimers-collapse-icon" />
                    <span className="subtimers-title">{t('stopwatchElement.steps')} ({completedCount}/{steps.length})</span>
                </button>
                {!stepsCollapsed && (
                    <div className="timer-subtimers-list">
                        {steps.map((step, index) => {
                            const average = step.timeHistory.length > 0
                                ? Math.round(step.timeHistory.reduce((total, time) => total + time, 0) / step.timeHistory.length)
                                : null;
                            return (
                                <div className={`timer-subtimer-item ${index === currentStepIndex ? 'active' : ''} ${step.completed ? 'completed' : ''}`} key={step.id}>
                                    <div className={`timer-subtimer-dot ${index === currentStepIndex ? 'active' : ''} ${step.completed ? 'completed' : ''}`}>
                                        {step.completed && <FontAwesomeIcon icon={faCheck} />}
                                    </div>
                                    <div className="timer-subtimer-info">
                                        <span className="timer-subtimer-text">{step.text}</span>
                                        <span className="timer-subtimer-time">{index === currentStepIndex ? formatTime(elapsedTime) : average === null ? '--' : t('stopwatchElement.averageShort', { time: formatTime(average) })}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>}

            {showStatsDialog && (
                <div className="timer-edit-overlay" onClick={() => setShowStatsDialog(false)}>
                    <div className="timer-stats-dialog" onClick={(event) => event.stopPropagation()}>
                        <div className="timer-edit-dialog-header">
                            <div><h3>{t('stopwatchElement.statistics')}</h3><p>{element.title}</p></div>
                            <button className="timer-edit-close-button" onClick={() => setShowStatsDialog(false)} aria-label={t('stopwatchElement.close')}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="timer-stats-summary">
                            <div className="timer-stat-item"><span>{t('stopwatchElement.completedSessions')}</span><strong>{sessions.filter((session) => session.completed).length}</strong></div>
                            <div className="timer-stat-item"><span>{t('stopwatchElement.totalTracked')}</span><strong>{formatTime(totalTracked)}</strong></div>
                            <div className="timer-stat-item"><span>{t('stopwatchElement.averageSession')}</span><strong>{formatTime(averageSession)}</strong></div>
                            <div className="timer-stat-item"><span>{t('stopwatchElement.recordedSteps')}</span><strong>{allTimes.length}</strong></div>
                        </div>
                        <div className="timer-stats-section-heading"><h4>{t('stopwatchElement.stepAverages')}</h4></div>
                        <div className="timer-stats-table">
                            <div className="timer-stats-table-row timer-stats-table-header">
                                <span>{t('stopwatchElement.step')}</span>
                                <span>{t('stopwatchElement.runs')}</span>
                                <span>{t('stopwatchElement.average')}</span>
                                <span>{t('stopwatchElement.best')}</span>
                            </div>
                            {steps.map((step) => {
                                const average = step.timeHistory.length > 0 ? Math.round(step.timeHistory.reduce((total, time) => total + time, 0) / step.timeHistory.length) : 0;
                                return <div className="timer-stats-table-row" key={step.id}><span className="timer-stats-step-name">{step.text}</span><span>{step.timeHistory.length}</span><span>{step.timeHistory.length ? formatTime(average) : '-'}</span><span>{step.timeHistory.length ? formatTime(Math.min(...step.timeHistory)) : '-'}</span></div>;
                            })}
                        </div>
                        <div className="timer-stats-section-heading"><h4>{t('stopwatchElement.sessions')}</h4></div>
                        <div className="timer-session-list">
                            {sessions.length === 0 && <p className="timer-stats-empty">{t('stopwatchElement.noSessions')}</p>}
                            {sessions.map((session) => {
                                const expanded = expandedSessionId === session.id;
                                return (
                                    <div className={`timer-session-item ${expanded ? 'expanded' : ''}`} key={session.id}>
                                        <button className="timer-session-trigger" onClick={() => setExpandedSessionId(expanded ? null : session.id)} aria-expanded={expanded}>
                                            <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} className="timer-session-chevron" />
                                            <span className="timer-session-identity"><strong>{formatDate(session.completedAt)}</strong><small>{formatTime(session.totalTime)}</small></span>
                                            <span className={`timer-session-result ${session.completed ? 'on-time' : 'abandoned'}`}>{session.completed ? t('stopwatchElement.completed') : t('stopwatchElement.abandoned')}</span>
                                        </button>
                                        {expanded && <div className="timer-session-details"><div className="timer-session-steps">{session.steps.map((step) => <div className="timer-session-step" key={step.stepId}><span className="timer-session-step-name"><strong>{step.text}</strong><small>{t(`stopwatchElement.status.${step.status}`)}</small></span><span><small>{t('stopwatchElement.time')}</small><strong>{step.status === 'skipped' ? '-' : formatTime(step.elapsedTime)}</strong></span></div>)}</div></div>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {showEditDialog && (
                <div className="timer-edit-overlay" onClick={() => setShowEditDialog(false)}>
                    <div className="timer-edit-dialog" onClick={(event) => event.stopPropagation()}>
                        <div className="timer-edit-dialog-header"><div><h3>{t('stopwatchElement.edit')}</h3><p>{editMode === 'simple' ? t('stopwatchElement.simple') : `${t('stopwatchElement.steps')} ${editSteps.length}`}</p></div><button className="timer-edit-close-button" onClick={() => setShowEditDialog(false)} aria-label={t('stopwatchElement.close')}><FontAwesomeIcon icon={faTimes} /></button></div>
                        <div className="timer-edit-field"><label>{t('stopwatchElement.name')}</label><input className="timer-edit-input" value={editTitle} onChange={(event) => setEditTitle(event.target.value)} /></div>
                        <div className="element-mode-selector" role="group" aria-label={t('stopwatchElement.mode')}>
                            <button type="button" className={editMode === 'simple' ? 'active' : ''} onClick={() => setEditMode('simple')}>{t('stopwatchElement.simple')}</button>
                            <button type="button" className={editMode === 'steps' ? 'active' : ''} onClick={() => setEditMode('steps')}>{t('stopwatchElement.withSteps')}</button>
                        </div>
                        {editMode === 'steps' && <div className="timer-edit-subtimers">
                            <div className="timer-edit-section-heading"><label>{t('stopwatchElement.steps')}</label><span>{editSteps.length}</span></div>
                            {editSteps.map((step, index) => <div className="timer-edit-subtimer-item" key={step.id}><span className="timer-edit-subtimer-number">{index + 1}</span><input className="timer-edit-subtimer-input" value={step.text} onChange={(event) => setEditSteps((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, text: event.target.value } : item))} /><button className="timer-remove-subtimer" onClick={() => setEditSteps((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={t('stopwatchElement.removeStep')}><FontAwesomeIcon icon={faTimes} /></button></div>)}
                            <div className="timer-add-subtimer"><span className="timer-edit-subtimer-number timer-edit-subtimer-number-add"><FontAwesomeIcon icon={faPlus} /></span><div className="timer-add-subtimer-controls"><input className="timer-edit-subtimer-input" value={newStep} onChange={(event) => setNewStep(event.target.value)} placeholder={t('stopwatchElement.addStepPlaceholder')} /><button className="timer-add-subtimer-button" onClick={() => { if (!newStep.trim()) return; setEditSteps((current) => [...current, { id: generateId(), text: newStep.trim(), timeHistory: [], completed: false }]); setNewStep(''); }} aria-label={t('stopwatchElement.addStep')}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                        </div>}
                        <div className="timer-edit-actions"><button onClick={() => setShowEditDialog(false)}>{t('stopwatchElement.cancel')}</button><button className="primary" onClick={saveEdit}>{t('stopwatchElement.save')}</button></div>
                    </div>
                </div>
            )}

            {showDeleteDialog && <ConfirmationDialog title={t('stopwatchElement.delete')} message={t('stopwatchElement.deleteConfirm')} onConfirm={() => deleteElement(element.id)} onCancel={() => setShowDeleteDialog(false)} isDanger />}
            {showListDialog && <ListSelectionDialog lists={lists} selectedListId={selectedListId} onSelectList={(listId) => { moveElementToList(element, listId); setShowListDialog(false); }} onClose={() => setShowListDialog(false)} />}
        </div>
    );
};
